/**
 * lib/hrAudit.ts
 * ------------------------------------------------------------------
 * Append-only audit logging for the HR PAYROLL domain.
 *
 * Sensitive payroll actions — activating a tax table, finalising a
 * pay run, editing a salary or deduction — write a row to hrAuditLog
 * with the actor, timestamp, and before/after values. This is the
 * record LKC shows BURS ("which rates did we use in May 2026?") and
 * the school's own internal control.
 *
 * Mirrors the academic lib/audit.ts pattern (fire-and-forget; a
 * logging failure must never block the action it describes). Writes
 * go through the hr.auditLog wrapper so the collection name and
 * timestamp conventions stay in one place. The hrAuditLog rules are
 * create-only for hr/admin and immutable thereafter.
 * ------------------------------------------------------------------ */
import { hr } from '@/features/hr/hrWrite';
import { logAudit } from '@/lib/audit';
import { stripUndefined } from '@/lib/sanitize';

export interface HrAuditActor {
  id: string;
  email?: string | null;
}

export interface RecordHrAuditInput {
  /** Verb phrase, e.g. 'taxTable.activate', 'payslip.finalise'. */
  action: string;
  actor: HrAuditActor | null;
  /** Subject reference: a staffId / payslipId / taxTableId. */
  subjectId?: string;
  before?: unknown;
  after?: unknown;
}

/** Write one audit entry. Never throws — logs to console on failure. */
export async function recordHrAudit(input: RecordHrAuditInput): Promise<void> {
  try {
    await hr.auditLog.add({
      action: input.action,
      actorId: input.actor?.id ?? 'unknown',
      actorEmail: input.actor?.email ?? undefined,
      subjectId: input.subjectId,
      before: input.before ?? undefined,
      after: input.after ?? undefined,
      at: new Date().toISOString(),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('hrAudit: failed to write audit entry', input.action, err);
  }

  // Mirror to the central activity log so HR actions surface in the
  // Activity Log screen alongside everything else. Independent of the
  // silo write above (the immutable hrAuditLog remains the BURS record).
  // logAudit is fire-and-forget and self-catching; we strip undefined so
  // the raw addDoc it uses never sees an unsupported value.
  const details = stripUndefined({ before: input.before, after: input.after });
  const entry = {
    action: input.action,
    category: 'hr' as const,
    actorId: input.actor?.id ?? 'unknown',
    ...(input.actor?.email ? { actorEmail: input.actor.email } : {}),
    ...(input.subjectId ? { targetId: input.subjectId } : {}),
    ...(Object.keys(details).length ? { details } : {}),
  };
  void logAudit(entry);
}
